import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { filterQueryKey } from '../lib/reactQueryHelpers';
import { ValidatedOrder } from '../types/orders';
import { useOrderCancelMutation } from './useOrderCancelMutation';
import { useOrderMutation } from './useOrderMutation';
import { Order, orderQuery } from './useOrders';
import { ProductType } from './useProducts';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';

export const MutationStateSchema = z.object({
  variables: z.object({
    existingOrderId: z.string(),
    newOrderData: z.unknown(),
  }),
});

export const getEditOrderMutationKey = (options?: { productId?: string }) =>
  ['editOrder', options?.productId].filter(Boolean);

/**
 * Edits an order by canceling the existing order and submitting a new one.
 */
export const useOrderEditMutation = (product: ProductType | undefined) => {
  const queryClient = useQueryClient();
  const { mutateAsync: cancelOrder } = useOrderCancelMutation({
    showToast: false,
    optimisticCancel: false,
  });
  const subaccount = useActiveSelectedSubaccount();
  const { mutateAsync: submitOrder } = useOrderMutation(product);

  return useMutation({
    mutationKey: getEditOrderMutationKey({ productId: product?.id }),
    mutationFn: async ({
      existingOrderId,
      newOrderData,
    }: {
      existingOrderId: string;
      newOrderData: ValidatedOrder;
    }) => {
      const queryKey = filterQueryKey(orderQuery({ subaccountId: subaccount?.id }).queryKey);
      // Optimistically update the the price, this avoids flicker when dragging orders on the TV chart.
      queryClient.setQueriesData({ queryKey }, (oldData: { pages: { orders: Order[] }[] }) => {
        const newPages = oldData.pages.map((page) => {
          return {
            ...page,
            orders: page.orders.map((order) => {
              if ('price' in newOrderData) {
                return {
                  ...order,
                  price: newOrderData.price,
                };
              }
              if ('stopPrice' in newOrderData) {
                return {
                  ...order,
                  stopPrice: newOrderData.stopPrice,
                };
              }
              return order;
            }),
          };
        });

        return { ...oldData, pages: newPages };
      });

      await cancelOrder([existingOrderId]);
      await submitOrder(newOrderData);
    },
  });
};
